// Pure helpers for the SPIN raw-export page. The download is a plain <a href> rather than an axios call
// (a ~100MB streamed ZIP the browser should own, not the app), so building that URL correctly IS the
// logic worth testing — a wrong parameter name yields a silent 400 halfway into a long request.
import type { SpinExportFileKey } from '../../../Generated/SpinExportFile.ts'
import { SpinExportFiles } from '../../../Generated/SpinExportFile.js'
import type { SpinRun } from '../../../Generated/SpinRun.ts'

/** Every file the bundle can carry, in the order the page lists them. */
export const EXPORT_FILE_KEYS = Object.keys(SpinExportFiles) as SpinExportFileKey[]

/**
 * The (algorithmType, asOf, amplicon) triple the export is scoped by — read off the selected run exactly
 * as `useSpinData`'s `runScope` does, so the bundle describes the same snapshot the rest of the panel is
 * showing. `asOf` is the run's `startedAt`: currency is by timestamp, never by run id, and the run is
 * only where a convenient timestamp comes from.
 */
export interface SpinExportScope {
  algorithmType: string
  asOf: string
  amplicon: string | null
}

export const spinExportScope = (run: SpinRun | null): SpinExportScope | null => {
  if (!run?.algorithmType || !run.startedAt) return null
  return { algorithmType: run.algorithmType, asOf: run.startedAt, amplicon: run.amplicon ?? null }
}

/**
 * The bundle download URL. `files` is repeated rather than comma-joined so a filename can never be
 * mistaken for a separator, and emitted in bundle order rather than the order the boxes were ticked —
 * the backend writes the entries in the order it receives them, so canonicalising here is what keeps the
 * archive's contents matching the list the page shows next to the button.
 */
export const buildSpinExportUrl = (apiUrl: string, scope: SpinExportScope, files: SpinExportFileKey[]): string => {
  const params = new URLSearchParams({ algorithmType: scope.algorithmType, asOf: scope.asOf })
  if (scope.amplicon) params.set('amplicon', scope.amplicon)
  EXPORT_FILE_KEYS.filter((key) => files.includes(key)).forEach((file) => params.append('files', file))
  return `${apiUrl}/admin/spin/export/bundle.zip?${params.toString()}`
}

/** The archive's entry names for a selection, in bundle order — README and manifest always ship. */
export const bundleContents = (files: SpinExportFileKey[]): string[] => [
  'README.txt',
  ...EXPORT_FILE_KEYS.filter((key) => files.includes(key)).map((key) => SpinExportFiles[key].fileName),
  'MANIFEST.txt'
]

/**
 * Rough total wait, summed from the per-file cost hints the backend publishes. Presented as a range
 * because the hints are measured orders of magnitude, not promises — the point is to set the
 * expectation "minutes, not seconds" before someone starts a download and assumes it hung.
 */
export const estimatedSeconds = (files: SpinExportFileKey[]): number =>
  files.reduce((total, key) => {
    const seconds = Number(/~(\d+)s/.exec(SpinExportFiles[key].costHint)?.[1] ?? 0)
    return total + seconds
  }, SNAPSHOT_SETUP_SECONDS)

/** Resolving the snapshot before any file is written — measured ~10s on the production snapshot. */
export const SNAPSHOT_SETUP_SECONDS = 10
